import type { SettingsPanel } from "@sagot/constants";
import { AppLink, cn, Icon } from "@sagot/ui";

/**
 * Which way the traffic goes for the Settings entries that talk to other systems (user decision, WP-CO-5): API keys are
 * INBOUND (your systems call Sagot), Webhooks are OUTBOUND EVENTS (Sagot tells your systems what happened), Connectors are
 * OUTBOUND CALLS (Sagot asks your systems), Integrations are SIGN-IN with a provider. Panels not listed have no label.
 */
export const PANEL_DIRECTION: Readonly<Partial<Record<SettingsPanel, "inbound" | "outboundEvents" | "signIn">>> = {
  api: "inbound",
  webhooks: "outboundEvents",
  integrations: "signIn",
};

export type DirectionKey = "inbound" | "outboundEvents" | "outboundCalls" | "signIn";

/** The small second line under a Settings menu entry. Plain `text-fg-muted` keeps 4.5:1 on the menu background in both themes. */
export function NavDirection({ label, direction }: { label: string; direction: DirectionKey }) {
  return (
    <span className="block text-xs font-normal text-fg-muted" data-direction={direction}>
      {label}
    </span>
  );
}

/**
 * The one-line note at the top of the API keys and Connectors pages: what this page is for, and the link to the page for the
 * other direction. The sentence comes in three parts (lead, link text, tail) so the link sits inside it in every language; with
 * no `href` (the member cannot open the other page) the link text stays plain.
 */
export function DirectionNote({ lead, linkLabel, tail, href, testId, className }: { lead: string; linkLabel: string; tail: string; href: string | null; testId: string; className?: string }) {
  return (
    <p className={cn("flex items-start gap-1.5 text-xs text-fg-muted", className)} data-testid={testId}>
      <Icon name="info" className="mt-0.5 size-3.5" />
      <span>
        {lead}
        {href ? (
          <AppLink href={href} className="font-semibold text-primary-ink underline underline-offset-2 hover:text-fg">
            {linkLabel}
          </AppLink>
        ) : (
          <strong className="font-semibold text-fg">{linkLabel}</strong>
        )}
        {tail}
      </span>
    </p>
  );
}
